//@ts-check
import "../../styles/AppRegistrationForm.css";

import React, { useState } from "react";
import env from "react-dotenv";
import { useMsal } from "@azure/msal-react";
import { loginRequest } from "../../auth/authConfig";

import { FormGroup, Label, Input, Alert } from "reactstrap";
import { Card, Spinner, Button, Form } from "react-bootstrap";

import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faExternalLinkAlt } from "@fortawesome/free-solid-svg-icons";

const appRegistrationUrl = (appId) =>
  `https://portal.azure.com/#view/Microsoft_AAD_RegisteredApps/ApplicationMenuBlade/~/Overview/appId/${appId}/isMSAApp~/false`;

const enterpriseAppUrl = (servicePrincipalId, appId) =>
  `https://portal.azure.com/#view/Microsoft_AAD_IAM/ManagedAppMenuBlade/~/Overview/objectId/${servicePrincipalId}/appId/${appId}`;

/**
 * Links to the app registration and/or enterprise app, depending on which ids are present.
 * @param {{ appId?: string, applicationId?: string | null, servicePrincipalId?: string | null }} app
 */
const PortalLinks = ({ app }) => (
  <>
    {app.applicationId && (
      <div className="arRow">
        <a href={appRegistrationUrl(app.appId)} target="_blank" rel="noreferrer">
          Open App Registration
          <FontAwesomeIcon icon={faExternalLinkAlt} className="arLinkIcon" />
        </a>
      </div>
    )}
    {app.servicePrincipalId && (
      <div className="arRow">
        <a href={enterpriseAppUrl(app.servicePrincipalId, app.appId)} target="_blank" rel="noreferrer">
          Open Enterprise Application
          <FontAwesomeIcon icon={faExternalLinkAlt} className="arLinkIcon" />
        </a>
      </div>
    )}
  </>
);

export const AppRegistrationForm = () => {
  const [displayName, setDisplayName] = useState("");
  const [loading, setLoading] = useState(false);

  const [message, setMessage] = useState(/** @type {null | {text: string, type: string}} */ (null));

  const [createdApp, setCreatedApp] = useState(
    /** @type {null | {
     *  displayName: string,
     *  appId: string,
     *  applicationId: string,
     *  servicePrincipalId: string
     * }} */ (null)
  );

  const [duplicates, setDuplicates] = useState(/** @type {any[]} */ ([]));

  const { instance, accounts } = useMsal();

  const baseUrl = env.API_BASE_URL || "http://localhost:7071";

  const handleSubmit = async (event) => {
    setLoading(true);
    try {
      if (event) event.preventDefault();

      setMessage(null);
      setCreatedApp(null);
      setDuplicates([]);

      if (!displayName || !displayName.trim()) {
        setMessage({ type: "warning", text: "Please enter a name for the app registration." });
        return;
      }

      const tokenResponse = await instance.acquireTokenSilent({
        ...loginRequest,
        account: accounts[0],
      });

      let response;
      try {
        response = await fetch(`${baseUrl}/api/appRegistrations`, {
          method: "POST",
          headers: {
            Authorization: `Bearer ${tokenResponse.accessToken}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            DisplayName: displayName.trim(),
          }),
        });
      } catch (e) {
        console.error(e);
        setMessage({ type: "danger", text: "Error communicating with server" });
        return;
      }

      if (response.status === 200 || response.status === 201) {
        setCreatedApp(await response.json());
      } else if (response.status === 409) {
        const json = await response.json();
        setMessage({ type: "warning", text: json.message });
        setDuplicates(Array.isArray(json.duplicates) ? json.duplicates : []);
      } else if (response.status === 400) {
        setMessage({ type: "warning", text: await response.text() });
      } else if (response.status === 403) {
        setMessage({ type: "danger", text: "You do not have authorization to do that." });
      } else {
        const err = await response.text();
        setMessage({ type: "danger", text: err || "Failed to create app registration." });
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClear = () => {
    setDisplayName("");
    setMessage(null);
    setCreatedApp(null);
    setDuplicates([]);
  };

  return (
    <div>
      <div className="formHeader">Create App Registration</div>

      <div className="AppRegistrationForm">
        <Form onSubmit={handleSubmit}>
          <FormGroup>
            <Label for="appDisplayName">App Name</Label>
            <Input
              type="text"
              id="appDisplayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              disabled={loading || !!createdApp}
              placeholder="Enter the display name for the new app"
            />
            <div className="arHelp">
              An app registration and enterprise application will be created with this name, and you will be added as
              the owner of both.
            </div>
          </FormGroup>

          <Button
            disabled={loading || !!createdApp}
            type="submit"
            className="button"
            style={{ width: "240px", height: "38px", margin: "2px" }}
          >
            {loading ? <Spinner animation="border" size="sm" /> : "Create App Registration"}
          </Button>

          <Button
            className="button"
            style={{ width: "auto", height: "38px", margin: "2px" }}
            onClick={handleClear}
            type="button"
            disabled={loading}
          >
            Clear
          </Button>
        </Form>

        {message && (
          <div className="mt-3">
            <Alert color={message.type}>{message.text}</Alert>
          </div>
        )}

        {duplicates.length > 0 && (
          <div style={{ marginTop: ".5em" }}>
            <Label>Existing apps with this name:</Label>
            {duplicates.map((d) => (
              <Card className="arCard" key={d.servicePrincipalId || d.applicationId || d.appId} style={{ padding: ".85em" }}>
                <Card.Title className="arCardTitle">{d.displayName}</Card.Title>
                <Card.Body style={{ padding: 0 }}>
                  <div className="arRow">
                    <span className="arLabel">App (client) Id:</span>
                    <span className="arValue">{d.appId}</span>
                  </div>
                  <PortalLinks app={d} />
                </Card.Body>
              </Card>
            ))}
          </div>
        )}

        {createdApp && (
          <div style={{ marginTop: "1em" }}>
            <Alert color="success">App registration '{createdApp.displayName}' was created successfully.</Alert>
            <Card className="arCard" style={{ padding: ".85em" }}>
              <Card.Title className="arCardTitle">{createdApp.displayName}</Card.Title>
              <Card.Body style={{ padding: 0 }}>
                <div className="arRow">
                  <span className="arLabel">App (client) Id:</span>
                  <span className="arValue">{createdApp.appId}</span>
                </div>
                <div className="arRow">
                  <span className="arLabel">Application Object Id:</span>
                  <span className="arValue">{createdApp.applicationId}</span>
                </div>
                <div className="arRow">
                  <span className="arLabel">Enterprise App Object Id:</span>
                  <span className="arValue">{createdApp.servicePrincipalId}</span>
                </div>
                <PortalLinks app={createdApp} />
              </Card.Body>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
};
